import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { authApi } from "../api/auth";
import type {
  CurrentUserResponse,
  SignInPayload,
  SignInResponse,
} from "../api/auth";
import { requireData, yieldToCancellation } from "./http";

export const authQueryKeys = {
  all: ["auth"] as const,
  currentUser: ["auth", "current-user"] as const,
  sessions: ["auth", "sessions"] as const,
};

export const currentUserQueryOptions = () =>
  queryOptions({
    queryKey: authQueryKeys.currentUser,
    queryFn: async ({ signal }) => {
      await yieldToCancellation(signal);
      return requireData(await authApi.getCurrentUser(signal));
    },
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

export const useCurrentUser = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({ ...currentUserQueryOptions(), enabled });

const currentUserFromSignIn = (
  response: SignInResponse,
): CurrentUserResponse => ({
  id: response.user.id,
  email: response.user.email,
  role: response.user.profile.role,
  profile: response.user.profile,
});

export const setSignedInUser = (
  client: QueryClient,
  response: SignInResponse,
) => {
  client.removeQueries();
  client.setQueryData(
    authQueryKeys.currentUser,
    currentUserFromSignIn(response),
  );
};

export const clearSignedOutUser = async (client: QueryClient) => {
  await client.cancelQueries();
  client.removeQueries();
};

export const invalidateCurrentUser = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: authQueryKeys.currentUser });

export const useSignIn = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, password }: SignInPayload) =>
      requireData(await authApi.signIn(email, password)),
    onSuccess: (response) => setSignedInUser(client, response),
  });
};

export const useSignOut = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => requireData(await authApi.signOut()),
    onSuccess: async () => clearSignedOutUser(client),
  });
};

export const isAuthStatus = (error: unknown, status: 401 | 403) =>
  Boolean(
    error &&
    typeof error === "object" &&
    "status" in error &&
    Number(error.status) === status,
  );

export const useAuthMethods = () => useQuery({
  queryKey: ["auth", "methods"],
  queryFn: async ({ signal }) => requireData(await authApi.getMethods(signal)),
  staleTime: 30_000,
});
